import { Sun, Leaf, Sparkles, Cloud, Moon, CloudRain } from "lucide-react";
import i18n from "@/i18n";

// Palette d'émotions du Cocon (valence 1→5 = « rayonnement » du Cocon pour la courbe).
export const MOODS = [
  { key: "rayonnant", Icon: Sun, valence: 5, ring: "from-amber-300 to-orange-500", glow: "hsl(38 95% 55% / 0.5)", text: "text-amber-200", dot: "#f5a623" },
  { key: "serein", Icon: Leaf, valence: 4, ring: "from-emerald-300 to-teal-500", glow: "hsl(160 70% 45% / 0.5)", text: "text-emerald-200", dot: "#34d399" },
  { key: "curieux", Icon: Sparkles, valence: 4, ring: "from-sky-300 to-indigo-500", glow: "hsl(210 90% 55% / 0.5)", text: "text-sky-200", dot: "#38bdf8" },
  { key: "pensif", Icon: Cloud, valence: 3, ring: "from-violet-300 to-purple-500", glow: "hsl(270 80% 60% / 0.5)", text: "text-violet-200", dot: "#a78bfa" },
  { key: "fatigue", Icon: Moon, valence: 2, ring: "from-indigo-300 to-slate-500", glow: "hsl(240 40% 55% / 0.5)", text: "text-indigo-200", dot: "#818cf8" },
  { key: "triste", Icon: CloudRain, valence: 1, ring: "from-slate-300 to-blue-500", glow: "hsl(215 40% 55% / 0.5)", text: "text-slate-200", dot: "#94a3b8" },
];

export const moodByKey = (key) => MOODS.find((m) => m.key === key) || MOODS[0];

// Étincelle accordée à l'humeur du jour (ton : vif / calme / doux selon l'émotion).
const MOOD_TONE = { rayonnant: "vivid", curieux: "vivid", serein: "calm", pensif: "soft", fatigue: "soft", triste: "soft" };
const TONE_SPARKS = {
  fr: {
    vivid: ["Comment naissent les galaxies ?", "Pourquoi les aurores boréales dansent-elles ?", "À quelle vitesse voyage la lumière ?", "Comment les volcans façonnent la Terre ?"],
    calm: ["Pourquoi la mer nous apaise-t-elle ?", "Comment les forêts poussent-elles en silence ?", "Pourquoi aime-t-on regarder les nuages ?"],
    soft: ["Pourquoi un câlin réconforte-t-il ?", "Comment le sommeil répare-t-il le corps ?", "Pourquoi la pluie a-t-elle une odeur ?", "Pourquoi les chats ronronnent-ils ?"],
  },
  en: {
    vivid: ["How are galaxies born?", "Why do the northern lights dance?", "How fast does light travel?", "How do volcanoes shape the Earth?"],
    calm: ["Why does the sea soothe us?", "How do forests grow in silence?", "Why do we love watching clouds?"],
    soft: ["Why does a hug comfort us?", "How does sleep repair the body?", "Why does rain have a smell?", "Why do cats purr?"],
  },
  es: {
    vivid: ["¿Cómo nacen las galaxias?", "¿Por qué bailan las auroras boreales?", "¿A qué velocidad viaja la luz?", "¿Cómo moldean los volcanes la Tierra?"],
    calm: ["¿Por qué el mar nos calma?", "¿Cómo crecen los bosques en silencio?", "¿Por qué nos gusta mirar las nubes?"],
    soft: ["¿Por qué reconforta un abrazo?", "¿Cómo repara el sueño el cuerpo?", "¿Por qué la lluvia tiene olor?", "¿Por qué ronronean los gatos?"],
  },
  nl: {
    vivid: ["Hoe worden sterrenstelsels geboren?", "Waarom dansen het noorderlicht?", "Hoe snel reist licht?", "Hoe vormen vulkanen de aarde?"],
    calm: ["Waarom kalmeert de zee ons?", "Hoe groeien bossen in stilte?", "Waarom kijken we graag naar wolken?"],
    soft: ["Waarom troost een knuffel?", "Hoe herstelt slaap het lichaam?", "Waarom heeft regen een geur?", "Waarom spinnen katten?"],
  },
};

export const moodSpark = (moodKey) => {
  const lang = (i18n.language || "fr").slice(0, 2);
  const pack = TONE_SPARKS[lang] || TONE_SPARKS.fr;
  const pool = pack[MOOD_TONE[moodKey] || "calm"] || pack.calm;
  return pool[Math.floor(Math.random() * pool.length)];
};

const TEXT = {
  fr: {
    title: "Comment va ton Cocon aujourd'hui ?",
    subtitle: "Choisis ce que tu ressens, juste pour toi.",
    explore: "Explorer cette étincelle",
    household: { title: "Météo du Foyer", desc: "Un regard doux sur la semaine de chaque explorateur.", none: "Pas encore de nouvelles cette semaine.", bright: "Une belle semaine lumineuse", serene: "Une semaine sereine", soft: "Une semaine tout en douceur", cocoon: "Une semaine à cocooner" },
    later: "Plus tard",
    historyTitle: "Histoire de ton Cocon",
    historyDesc: "L'évolution de tes ressentis, au fil de tes passages.",
    empty: "Ton Cocon commence tout juste son histoire. Reviens demain pour la voir grandir.",
    entries: "passages",
    recent: "Tes derniers jours",
    labels: { rayonnant: "Rayonnant", serein: "Serein", curieux: "Curieux", pensif: "Pensif", fatigue: "Fatigué", triste: "Nuageux" },
    words: {
      rayonnant: "Quelle belle lumière aujourd'hui — laissons-la éclairer une nouvelle curiosité.",
      serein: "Un Cocon paisible est un terrain fertile. Explorons en douceur.",
      curieux: "La curiosité est ta plus belle boussole. Suis-la, je te suis.",
      pensif: "Les pensées qui flottent cachent souvent de belles questions. Tirons-en une.",
      fatigue: "Même fatigué, un petit émerveillement fait du bien. On y va tout doux.",
      triste: "Merci de me l'avoir confié. Peut-être qu'une douce découverte réchauffera un peu ta journée.",
    },
  },
  en: {
    title: "How is your Cocoon today?",
    subtitle: "Choose what you feel — just for you.",
    explore: "Explore this spark",
    household: { title: "Home Weather", desc: "A gentle look at each explorer's week.", none: "No news yet this week.", bright: "A bright, radiant week", serene: "A serene week", soft: "A soft, gentle week", cocoon: "A week for cocooning" },
    later: "Later",
    historyTitle: "Your Cocoon's story",
    historyDesc: "How your feelings evolve, visit after visit.",
    empty: "Your Cocoon is just beginning its story. Come back tomorrow to watch it grow.",
    entries: "check-ins",
    recent: "Your recent days",
    labels: { rayonnant: "Radiant", serein: "Serene", curieux: "Curious", pensif: "Pensive", fatigue: "Tired", triste: "Cloudy" },
    words: {
      rayonnant: "What lovely light today — let's use it to spark a new curiosity.",
      serein: "A peaceful Cocoon is fertile ground. Let's explore gently.",
      curieux: "Curiosity is your finest compass. Follow it — I'll follow you.",
      pensif: "Floating thoughts often hide beautiful questions. Let's pull on one.",
      fatigue: "Even when tired, a little wonder feels good. Let's go softly.",
      triste: "Thank you for trusting me with that. Maybe a gentle discovery can warm your day a little.",
    },
  },
  es: {
    title: "¿Cómo está tu Cocoon hoy?",
    subtitle: "Elige lo que sientes, solo para ti.",
    explore: "Explorar esta chispa",
    household: { title: "El Tiempo del Hogar", desc: "Una mirada tierna a la semana de cada explorador.", none: "Aún no hay novedades esta semana.", bright: "Una semana luminosa", serene: "Una semana serena", soft: "Una semana muy suave", cocoon: "Una semana para arroparse" },
    later: "Más tarde",
    historyTitle: "La historia de tu Cocoon",
    historyDesc: "Cómo evolucionan tus emociones, visita tras visita.",
    empty: "Tu Cocoon apenas empieza su historia. Vuelve mañana para verla crecer.",
    entries: "registros",
    recent: "Tus últimos días",
    labels: { rayonnant: "Radiante", serein: "Sereno", curieux: "Curioso", pensif: "Pensativo", fatigue: "Cansado", triste: "Nublado" },
    words: {
      rayonnant: "Qué hermosa luz hoy — usémosla para encender una nueva curiosidad.",
      serein: "Un Cocoon en calma es tierra fértil. Exploremos con suavidad.",
      curieux: "La curiosidad es tu mejor brújula. Síguela — yo te sigo.",
      pensif: "Los pensamientos que flotan esconden bellas preguntas. Tiremos de una.",
      fatigue: "Aun cansado, un pequeño asombro sienta bien. Vamos con calma.",
      triste: "Gracias por confiármelo. Quizá un descubrimiento suave caliente un poco tu día.",
    },
  },
  nl: {
    title: "Hoe gaat het met je Cocoon vandaag?",
    subtitle: "Kies wat je voelt — alleen voor jou.",
    explore: "Deze vonk verkennen",
    household: { title: "Het Weer in Huis", desc: "Een zachte blik op de week van elke verkenner.", none: "Deze week nog geen nieuws.", bright: "Een stralende week", serene: "Een serene week", soft: "Een zachte week", cocoon: "Een week om te cocoonen" },
    later: "Later",
    historyTitle: "Het verhaal van je Cocoon",
    historyDesc: "Hoe je gevoelens evolueren, bezoek na bezoek.",
    empty: "Je Cocoon begint net zijn verhaal. Kom morgen terug om het te zien groeien.",
    entries: "check-ins",
    recent: "Je recente dagen",
    labels: { rayonnant: "Stralend", serein: "Sereen", curieux: "Nieuwsgierig", pensif: "Peinzend", fatigue: "Moe", triste: "Bewolkt" },
    words: {
      rayonnant: "Wat een mooi licht vandaag — laten we er een nieuwe nieuwsgierigheid mee ontsteken.",
      serein: "Een rustige Cocoon is vruchtbare grond. Laten we zachtjes verkennen.",
      curieux: "Nieuwsgierigheid is je mooiste kompas. Volg het — ik volg jou.",
      pensif: "Zwevende gedachten verbergen vaak mooie vragen. Laten we er één uittrekken.",
      fatigue: "Zelfs moe doet een beetje verwondering goed. We gaan rustig aan.",
      triste: "Bedankt dat je me dat toevertrouwt. Misschien verwarmt een zachte ontdekking je dag een beetje.",
    },
  },
};

export const moodText = () => {
  const lang = (i18n.language || "fr").slice(0, 2);
  return TEXT[lang] || TEXT.fr;
};
